import { NavLink, Stack, Tooltip, Box, ActionIcon, Text } from "@mantine/core";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconInbox,
  IconUsers,
  IconBook2,
  IconChartBar,
  IconSettings,
  IconShieldCog,
  IconChevronsLeft,
  IconChevronsRight,
} from "@tabler/icons-react";
import { useUiStore } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";

const allNavItems = [
  { label: "Inbox", icon: IconInbox, path: "/inbox" },
  { label: "Customers", icon: IconUsers, path: "/customers" },
  { label: "Knowledge Base", icon: IconBook2, path: "/knowledge-base" },
  { label: "Analytics", icon: IconChartBar, path: "/analytics" },
  { label: "Settings", icon: IconSettings, path: "/settings" },
  { label: "Admin", icon: IconShieldCog, path: "/admin" },
];

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const isSystemAdmin = useAuthStore((s) => s.isSystemAdmin);

  // Settings stays visible for every role — the page itself hides the
  // sections a role can't use (Profile applies to everyone).
  const navItems = allNavItems.filter(
    (item) => item.path !== "/admin" || isSystemAdmin,
  );

  return (
    <Box
      component="nav"
      w={collapsed ? 56 : 240}
      h="100%"
      style={{
        borderRight: "1px solid var(--mantine-color-default-border)",
        transition: "width 200ms ease",
        overflow: "hidden",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Logo area */}
      <Box p="sm" pb={0}>
        <Box
          py="xs"
          px={collapsed ? 0 : "xs"}
          style={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start" }}
        >
          {collapsed ? (
            <Text fw={700} size="lg" c="indigo">
              H
            </Text>
          ) : (
            <Text fw={700} size="lg" c="indigo">
              HelpPilot
            </Text>
          )}
        </Box>
      </Box>

      {/* Navigation links */}
      <Stack gap={4} p="sm" style={{ flex: 1 }}>
        {navItems.map((item) => {
          const active = location.pathname.startsWith(item.path);
          const navLink = (
            <NavLink
              key={item.path}
              label={collapsed ? undefined : item.label}
              leftSection={<item.icon size={20} stroke={1.5} />}
              active={active}
              onClick={() => navigate(item.path)}
              variant="light"
              style={{
                borderRadius: "var(--mantine-radius-md)",
                justifyContent: collapsed ? "center" : undefined,
              }}
            />
          );

          if (collapsed) {
            return (
              <Tooltip key={item.path} label={item.label} position="right" withArrow>
                {navLink}
              </Tooltip>
            );
          }

          return navLink;
        })}
      </Stack>

      {/* Collapse toggle */}
      <Box p="sm" style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}>
        <Tooltip label={collapsed ? "Expand sidebar" : "Collapse sidebar"} position="right" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={toggleSidebar}
            w="100%"
            style={{ display: "flex", justifyContent: "center" }}
          >
            {collapsed ? (
              <IconChevronsRight size={18} stroke={1.5} />
            ) : (
              <IconChevronsLeft size={18} stroke={1.5} />
            )}
          </ActionIcon>
        </Tooltip>
      </Box>
    </Box>
  );
}
